import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, Stars, ChevronDown } from 'lucide-react';
import { useTheme, Theme } from '../contexts/ThemeContext';

const themes: { value: Theme; label: string; icon: typeof Sun; color: string }[] = [
  { value: 'light', label: 'Chiaro', icon: Sun, color: 'text-yellow-500' },
  { value: 'midnight', label: 'Midnight', icon: Stars, color: 'text-blue-400' },
  { value: 'dark', label: 'Scuro', icon: Moon, color: 'text-violet-400' },
];

export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentTheme = themes.find(t => t.value === theme) || themes[0];
  const CurrentIcon = currentTheme.icon;

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.08] hover:bg-gray-200 dark:hover:bg-white/[0.1] transition-colors"
        whileTap={{ scale: 0.95 }}
        aria-label="Seleziona tema"
      >
        <CurrentIcon size={18} className={currentTheme.color} />
        <span className="text-sm font-medium text-gray-700 dark:text-white/70 hidden sm:inline">
          {currentTheme.label}
        </span>
        <ChevronDown
          size={14}
          className={`text-gray-500 dark:text-white/40 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-40 py-1.5 bg-white dark:bg-[#25252d] rounded-xl border border-gray-200 dark:border-white/[0.08] shadow-xl z-50"
          >
            {themes.map(({ value, label, icon: Icon, color }) => {
              const isSelected = theme === value;
              return (
                <button
                  key={value}
                  onClick={() => {
                    setTheme(value);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                    isSelected
                      ? 'bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400'
                      : 'text-gray-700 dark:text-white/70 hover:bg-gray-50 dark:hover:bg-white/[0.04]'
                  }`}
                >
                  <Icon size={16} className={isSelected ? 'text-orange-500 dark:text-orange-400' : color} />
                  <span>{label}</span>
                  {isSelected && (
                    <motion.div
                      layoutId="theme-selected"
                      className="ml-auto w-1.5 h-1.5 rounded-full bg-orange-500"
                    />
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Simple cycle button for mobile/compact views
export function ThemeCycleButton() {
  const { theme, cycleTheme } = useTheme();
  const currentTheme = themes.find(t => t.value === theme) || themes[0];
  const CurrentIcon = currentTheme.icon;

  return (
    <motion.button
      onClick={cycleTheme}
      className="p-2.5 rounded-xl bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.08] hover:bg-gray-200 dark:hover:bg-white/[0.1] transition-colors"
      whileTap={{ scale: 0.9 }}
      aria-label="Cambia tema"
    >
      <CurrentIcon size={18} className={currentTheme.color} />
    </motion.button>
  );
}
